import type { PointerEvent } from 'react'
import { useState } from 'react'
import type { TPanelPlacement } from 'stores/detailsPanelStore/detailsPanelStore'
import { detailsPanelStore, openDetailsPanel } from 'stores/detailsPanelStore/detailsPanelStore'
import { getPanelStateAfterDrag } from './getPanelStateAfterDrag'

type TPanelDrag = { startPointer: number; startSize: number; pointer: number }

// The panel grows as the pointer moves toward the open side: left for the right panel, up for the bottom panel
const getDragSize = ({ startPointer, startSize, pointer }: TPanelDrag) => startSize + startPointer - pointer

export type TPanelDragHandlers = ReturnType<ReturnType<typeof usePanelDrag>['getDragHandlers']>

export const usePanelDrag = ({
  placement,
  isEnabled,
}: {
  placement: TPanelPlacement
  isEnabled: boolean
}) => {
  const [drag, setDrag] = useState<TPanelDrag | null>(null)
  const getPointer = (event: PointerEvent) => (placement === 'right' ? event.clientX : event.clientY)

  const getDragHandlers = ({ isOpen, size, maxSize }: { isOpen: boolean; size: number; maxSize: number }) => {
    const endDrag = (dragSize: number) => {
      detailsPanelStore.setState((s) => ({
        ...s,
        ...getPanelStateAfterDrag({ placement, size: dragSize, maxSize }),
      }))

      setDrag(null)
    }

    return {
      onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
        if (!isEnabled || !event.isPrimary) return

        event.preventDefault()

        event.currentTarget.setPointerCapture(event.pointerId)

        setDrag({ startPointer: getPointer(event), startSize: size, pointer: getPointer(event) })
      },
      onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
        if (!drag) return

        const nextDrag = { ...drag, pointer: getPointer(event) }

        setDrag(nextDrag)

        if (!isOpen && getDragSize(nextDrag) > 0) openDetailsPanel()
      },
      onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
        if (drag) endDrag(getDragSize({ ...drag, pointer: getPointer(event) }))
      },
      onPointerCancel: () => {
        if (drag) endDrag(getDragSize(drag))
      },
    }
  }

  return { isDragging: drag !== null, sizeDuringDrag: drag && getDragSize(drag), getDragHandlers }
}
